import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import styled, { css, useTheme } from "styled-components";
import { MdClose } from "react-icons/md";

import searchIcon from "assets/icons/Search-Icon-White.svg";
import { searchDocuments, setSearchMode } from "redux/actions/search";
import { clearErrors } from "redux/actions/errors";
import { RootState } from "redux/reducers";

import ModeSwitch, { SearchMode } from "./ModeSwitch";
import SearchDropdown, { Suggestions } from "./SearchDropdown";

// The dropdown asks for reports from this many letters, after a pause in typing
const SUGGEST_MIN_LETTERS = 4;
const SUGGEST_DELAY = 300;

// Dropdown width, and its margin from the window's edges
const DROPDOWN_WIDTH = 700;
const WINDOW_GUTTER = 16;

// Windows draws the text cursor by inverting what's under it, which on the field's
// mid blue leaves a mid colour that's hard to see. This one is white with a dark
// outline; browsers that can't show it use the normal text cursor.
const TEXT_CURSOR_SVG =
  "<svg xmlns='http://www.w3.org/2000/svg' width='12' height='22' viewBox='0 0 12 22'>" +
  "<path d='M3 2h6M6 2v18M3 20h6' fill='none' stroke='#14202e' stroke-width='3.6'/>" +
  "<path d='M3 2h6M6 2v18M3 20h6' fill='none' stroke='#fff' stroke-width='1.6'/></svg>";
const TEXT_CURSOR = `url("data:image/svg+xml,${encodeURIComponent(TEXT_CURSOR_SVG)}") 6 11, text`;

type SearchBarProps = {
  hidden: boolean;
  panelOpen: boolean;
  setPanelOpen: Dispatch<SetStateAction<boolean>>;
  onTogglePanel: () => void;
};

const SearchBar: React.FC<SearchBarProps> = ({
  hidden,
  panelOpen,
  setPanelOpen,
  onTogglePanel,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme: any = useTheme();
  const errors = useSelector((state: RootState) => state.errors);
  // Search state saved in the browser before the switch existed has no mode
  const mode = useSelector((state: RootState) => state.search.mode || "exact");
  const setMode = (newMode: SearchMode) => dispatch(setSearchMode(newMode));
  const [query, setQuery] = useState<string>("");
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const [suggestions, setSuggestions] = useState<Suggestions | null>(null);
  const [dropdownBox, setDropdownBox] = useState({
    left: 0,
    width: DROPDOWN_WIDTH,
  });
  const wrapperRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const requestIdRef = useRef<number>(0);

  const showDropdown =
    dropdownOpen &&
    suggestions !== null &&
    query.trim().length >= SUGGEST_MIN_LETTERS;

  // Close the small-screen panel and the dropdown on a tap outside them
  useEffect(() => {
    if (!panelOpen && !dropdownOpen) return;

    const handleOutside = (e: MouseEvent | TouchEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setPanelOpen(false);
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("touchstart", handleOutside);

    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("touchstart", handleOutside);
    };
  }, [panelOpen, dropdownOpen, setPanelOpen]);

  // Ask for the dropdown's reports after a pause in typing. Only the answer to
  // the latest request is used.
  useEffect(() => {
    const trimmed = query.trim();
    const requestId = ++requestIdRef.current;

    if (!dropdownOpen || trimmed.length < SUGGEST_MIN_LETTERS) {
      setSuggestions(null);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const { data } = await axios.post("/search/suggest", {
          query: trimmed,
          mode,
        });
        if (requestId === requestIdRef.current) setSuggestions(data);
      } catch (error) {
        // the dropdown doesn't show errors; Enter still opens the results page
        if (requestId === requestIdRef.current) setSuggestions(null);
      }
    }, SUGGEST_DELAY);

    return () => clearTimeout(timer);
  }, [query, mode, dropdownOpen]);

  // Keep the dropdown inside the window: 700px wide under the field, or the
  // header's full width at 900px and narrower
  useLayoutEffect(() => {
    if (!showDropdown) return;

    const place = () => {
      if (!formRef.current) return;

      const windowWidth = document.documentElement.clientWidth;
      const formLeft = formRef.current.getBoundingClientRect().left;
      const narrow = window.matchMedia(`(max-width: ${theme.bp.md}px)`)
        .matches;
      const width = narrow
        ? windowWidth - 2 * WINDOW_GUTTER
        : Math.min(DROPDOWN_WIDTH, windowWidth - 2 * WINDOW_GUTTER);
      const left = narrow
        ? WINDOW_GUTTER - formLeft
        : Math.min(0, windowWidth - WINDOW_GUTTER - width - formLeft);

      setDropdownBox({ left, width });
    };

    place();
    window.addEventListener("resize", place);

    return () => window.removeEventListener("resize", place);
  }, [showDropdown, theme]);

  const closeAll = () => {
    setPanelOpen(false);
    setDropdownOpen(false);
    if (inputRef.current) inputRef.current.blur();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    dispatch(clearErrors());
    setDropdownOpen(false);

    const body = {
      query,
      mode,
    };

    const found: any = await dispatch(searchDocuments(body, navigate));
    if (found) closeAll();
  };

  // Focus during the tap itself, or iPhones won't open the keyboard
  const handleIconClick = () => {
    if (!panelOpen && inputRef.current) inputRef.current.focus();
    if (panelOpen) setDropdownOpen(false);
    onTogglePanel();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") closeAll();
  };

  // From the dropdown: "Did you mean" and "Search with Fuzzy"
  const handleSuggestion = (suggestion: string) => {
    setQuery(suggestion);
    if (inputRef.current) inputRef.current.focus();
  };

  const handleModeFromDropdown = (newMode: SearchMode) => {
    setMode(newMode);
    if (inputRef.current) inputRef.current.focus();
  };

  if (hidden) {
    return null;
  } else {
    return (
      <Wrapper ref={wrapperRef}>
        <IconButton
          type="button"
          aria-label={panelOpen ? "Close search" : "Search"}
          aria-expanded={panelOpen}
          onClick={handleIconClick}
        >
          {panelOpen ? <MdClose /> : <img src={searchIcon} alt="" />}
        </IconButton>
        <Panel $open={panelOpen} onKeyDown={handleKeyDown}>
          <Form ref={formRef} autoComplete="off" onSubmit={handleSubmit}>
            <Input
              id="searchbar"
              ref={inputRef}
              type="text"
              name="query"
              onChange={(e) => {
                setQuery(e.target.value);
                setDropdownOpen(true);
              }}
              onFocus={() => setDropdownOpen(true)}
              value={query}
              placeholder={
                mode === "exact"
                  ? "Search All Reports (exact match)"
                  : "Search All Reports"
              }
              $active={showDropdown}
            />
            <Controls>
              <FieldSwitch mode={mode} setMode={setMode} />
              <SearchButton type="submit" aria-label="Search">
                <img src={searchIcon} alt="search glass" />
              </SearchButton>
            </Controls>
            <Error>{errors.query}</Error>
            {showDropdown && suggestions && (
              <SearchDropdown
                suggestions={suggestions}
                mode={mode}
                setMode={handleModeFromDropdown}
                box={dropdownBox}
                onSuggestion={handleSuggestion}
                onNavigate={closeAll}
              />
            )}
          </Form>
        </Panel>
      </Wrapper>
    );
  }
};

const Wrapper = styled.div`
  position: relative;
  flex: 1 1 280px;
  min-width: 0;
  max-width: 640px;
  height: 38px;
  margin: auto 24px auto 32px;

  @media screen and (max-width: ${(props) => props.theme.bp.md}px) {
    margin: auto 12px;
  }

  @media screen and (max-width: ${(props) => props.theme.bp.xs}px) {
    position: static;
    flex: none;
    height: auto;
    margin: auto 0 auto auto;
  }
`;

const IconButton = styled.button`
  display: none;

  @media screen and (max-width: ${(props) => props.theme.bp.xs}px) {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    padding: 0;
    background-color: rgba(255, 255, 255, 0.14);
    border: 1px solid rgba(255, 255, 255, 0.24);
    border-radius: 999px;
    color: #fff;
    transition: background-color 0.2s;

    &:hover {
      background-color: rgba(255, 255, 255, 0.18);
    }

    img,
    svg {
      width: 22px;
      height: 22px;
    }
  }
`;

// At 500px and narrower this is the panel under the header. It stays in the
// page at zero opacity so the field can take focus during the tap.
const Panel = styled.div<{ $open: boolean }>`
  height: 100%;

  @media screen and (max-width: ${(props) => props.theme.bp.xs}px) {
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    height: auto;
    padding: 12px 2rem;
    background-color: ${(props) => props.theme.colors.idataDarkBlue};
    border-top: 1px solid rgba(255, 255, 255, 0.18);
    opacity: ${(props) => (props.$open ? 1 : 0)};
    pointer-events: ${(props) => (props.$open ? "auto" : "none")};
    transition: opacity 0.18s;
  }
`;

const Form = styled.form`
  position: relative;
  height: 38px;
`;

const Input = styled.input<{ $active: boolean }>`
  width: 100%;
  height: 38px;
  padding: 0 200px 0 20px;
  background-color: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.24);
  border-radius: 999px;
  color: #fff;
  font-family: inherit;
  font-size: 15.12px;
  cursor: ${TEXT_CURSOR};
  transition: background-color 0.2s, border-color 0.2s;

  &::placeholder {
    color: #fff;
    opacity: 0.72;
  }

  &:hover {
    background-color: rgba(255, 255, 255, 0.18);
  }

  &:focus {
    background-color: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.65);
  }

  ${(props) =>
    props.$active &&
    css`
      &,
      &:hover,
      &:focus {
        background-color: rgba(255, 255, 255, 0.22);
        border-color: rgba(255, 255, 255, 0.65);
      }
    `}

  @media screen and (max-width: ${(props) => props.theme.bp.md}px) {
    padding-right: 56px;
  }

  @media screen and (max-width: ${(props) => props.theme.bp.xs}px) {
    padding-right: 190px;
  }
`;

const Controls = styled.div`
  position: absolute;
  top: 1px;
  right: 1px;
  bottom: 1px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

// From 501 to 900px the switch is in the dropdown's header row instead
const FieldSwitch = styled(ModeSwitch)`
  @media screen and (min-width: ${(props) => props.theme.bp.xs + 1}px) and (max-width: ${(props) => props.theme.bp.md}px) {
    display: none;
  }
`;

const SearchButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 54px;
  height: 100%;
  padding: 0;
  background-color: rgba(0, 0, 0, 0.12);
  border-radius: 0 999px 999px 0;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(0, 0, 0, 0.22);
  }

  img {
    width: 22px;
    height: 22px;
  }

  @media screen and (max-width: ${(props) => props.theme.bp.md}px) {
    width: 44px;
  }
`;

const Error = styled.p`
  color: ${(props) => props.theme.colors.red};
  font-size: 1.4rem;
  font-weight: 700rem;
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  margin: 2px 0 0;
  text-align: center;
`;

export default SearchBar;
